import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpMainService } from '../http-main.service';
import {
  AccountListRequestModel,
  AccountTypeRequestModel,
  AssetsResponseModel,
  ChangePasswordAccountRequestModel,
  CreateAccountRequestModel,
  AccountResponseModel,
  DealsResponseModel,
  ListModel,
  PathRequestModel,
  PlatformResponseModel,
  StatisticsResponseModel,
  SymbolDetailsRequestModel,
  SymbolRequestModel,
  SymbolsResponseModel,
  UpdateLeverageRequestModel,
  Wallet,
  InstrumentCommissionResponseModel,
  PositionResponseModel,
} from '@itb-monorepo/models';
import { CategoryEnum, KindEnum } from '@itb-monorepo/enums';
import { UsersService } from './users.service';
import { formatDate } from '@angular/common';
import { InstrumentCommissionsRequest } from '@itb-monorepo/models';

@Injectable()
export class TradesService {
  private _HttpMainService = inject(HttpMainService);
  private usersService = inject(UsersService);
  baseUrl = '/trade/';

  getPlatforms(): Observable<ListModel<PlatformResponseModel>> {
    return this._HttpMainService.get(this.baseUrl + 'v1/platforms');
  }
  getAccountsList(
    req?: AccountListRequestModel
  ): Observable<ListModel<AccountResponseModel>> {
    if (req == undefined) {
      req = {
        skip: 0,
        take: 50,
        filter: `kind:in("${KindEnum.REAL.toUpperCase()}")`,
      };
    }

    const url = `${this.baseUrl}v1/users/current/accounts`;
    const params: any = {
      skip: req.skip,
      take: req.take,
      sort: 'desc(created_at)',
      include: 'group',
    };
    if (req.filter) {
      params.filter = req.filter;
    }
    return this._HttpMainService.get(url, params);
  }
  createTradingAccount(
    query: CreateAccountRequestModel,
    userId: string
  ): Observable<AccountResponseModel> {
    const url = `${this.baseUrl}v1/users/${userId}/accounts?include=group`;
    return this._HttpMainService.post(url, query);
  }
  getAccount(path: PathRequestModel): Observable<AccountResponseModel> {
    const url = `${
      this.baseUrl
    }v1/platforms/${path.platform_code.toUpperCase()}/accounts/${
      path.account_id
    }?include=group`;
    return this._HttpMainService.get(url);
  }
  getStatistics(path: PathRequestModel): Observable<StatisticsResponseModel> {
    const url = `${this.baseUrl}v1/users/${path.user_id}/statistics`;
    let params: any = {};
    if (path.account_id) {
      params.filter = `account_id:eq("${path.account_id}"),`;
    }
    if (path.from) {
      params.filter =
        (params.filter || '') +
        `closed_at:gte("${formatDate(
          new Date(path.from * 1000),
          'yyyy-MM-ddTHH:mm:ss',
          'en_US'
        )}")`;
    }
    if (path.to) {
      params.filter =
        (params.filter || '') +
        `closed_at:lte("${formatDate(
          new Date(path.to * 1000),
          'yyyy-MM-ddTHH:mm:ss',
          'en_US'
        )}")`;
    }
    return this._HttpMainService.get(url, params);
  }
  getAssets(): Observable<ListModel<AssetsResponseModel>> {
    const url = `${this.baseUrl}v1/users/current/assets`;
    return this._HttpMainService.get(url);
  }
  getAssetsByAccount(
    accountId: string
  ): Observable<ListModel<AssetsResponseModel>> {
    const url = `${this.baseUrl}v1/platforms/MQ/accounts/${accountId}/assets`;
    return this._HttpMainService.get(url);
  }
  getDeals(params: any): Observable<ListModel<DealsResponseModel>> {
    const url = `${
      this.baseUrl
    }v1/platforms/${params.platform_code.toUpperCase()}/accounts/${
      params.account_id
    }/trades`;

    return this._HttpMainService.get(url, {
      skip: params.skip,
      take: params.take,
      sort: 'desc(opened_at)',
      include: params.include ?? 'account',
      ...(params.filter ? { filter: params.filter } : {}),
    });
  }

  getWallet(): Observable<Wallet> {
    const url = `${this.baseUrl}v1/wallets/current`;
    return this._HttpMainService.get(url);
  }

  updateLeverageOfAccount(
    path: PathRequestModel,
    inputs: UpdateLeverageRequestModel
  ): Observable<AccountResponseModel> {
    const url = `${
      this.baseUrl
    }v1/platforms/${path.platform_code.toUpperCase()}/accounts/${
      path.account_id
    }`;
    return this._HttpMainService.put(url, inputs);
  }
  changePasswordOfAccount(
    path: PathRequestModel,
    inputs: ChangePasswordAccountRequestModel
  ): Observable<AccountResponseModel> {
    const url = `${
      this.baseUrl
    }v1/platforms/${path.platform_code.toUpperCase()}/accounts/${
      path.account_id
    }`;
    return this._HttpMainService.patch(url, inputs);
  }
  getGroups(
    section?: string,
    kind?: string
  ): Observable<ListModel<AccountTypeRequestModel>> {
    const qParams = {
      sort: `asc(priority)`,
      filter: kind ? `account_kinds:cnt("${kind}")` : undefined,
    };
    const url = `${this.baseUrl}v1/sections/${
      section ? section : this.usersService.currentUser?.section_slug
    }/groups`;
    return this._HttpMainService.get(url, qParams);
  }
  getPublicGroups(): Observable<ListModel<AccountTypeRequestModel>> {
    const qParams: any = {
      sort: `asc(priority)`,
    };
    const url = `${this.baseUrl}v1/groups`;
    return this._HttpMainService.get(url, qParams);
  }
  getSymbolsList(
    params: SymbolRequestModel
  ): Observable<ListModel<SymbolsResponseModel>> {
    const url = `${this.baseUrl}v1/instruments`;

    const filters: string[] = [];

    if (params.category !== CategoryEnum.ALL) {
      filters.push(`category:eq("${params.category}")`);
    }

    if (params.search?.trim()) {
      filters.push(`symbol:like("${params.search.trim()}")`);
    }

    const requestParams: any = {
      skip: params.skip,
      take: params.take,
      platform_code: params.platform_code,
    };

    if (filters.length) {
      requestParams.filter = filters.join(',');
    }

    return this._HttpMainService.get(url, requestParams);
  }

  getSymbolDetails(
    data: SymbolDetailsRequestModel
  ): Observable<SymbolsResponseModel> {
    const url = `${this.baseUrl}v1/instruments/${data.symbol_name}`;
    return this._HttpMainService.get(url, {
      candle_frame: data.candle_frame,
      with_changes: data.with_changes,
    });
  }
  getInstrumentCommissionsList(
    introducer_user_id: string
  ): Observable<ListModel<InstrumentCommissionResponseModel>> {
    const url = `${this.baseUrl}v1/introducers/${introducer_user_id}/instruments`;
    return this._HttpMainService.get(url, { include: 'instrument' });
  }
  updateIntroducer(
    introducer_user_id: string,
    model: InstrumentCommissionsRequest
  ): Observable<InstrumentCommissionResponseModel> {
    const url = `${this.baseUrl}v1/introducers/${introducer_user_id}/instruments`;
    return this._HttpMainService.put(url, model);
  }
  resetToDefaultInstrumentCommission(
    introducer_user_id: string
  ): Observable<ListModel<InstrumentCommissionResponseModel>> {
    const url = `${this.baseUrl}v1/introducers/${introducer_user_id}/instruments`;
    return this._HttpMainService.delete(url);
  }
  getPositionsList(params: any): Observable<ListModel<PositionResponseModel>> {
    const url = `${this.baseUrl}v1/platforms/${params.platform_code}/accounts/${params.accountId}/positions`;
    return this._HttpMainService.get(url, {
      skip: params.skip,
      take: params.take,
      filter: params.filter,
    });
  }
}
